import { useEffect } from 'react';
import { isIdentificationAllowed } from '../services/identificationConsent';
import { logger } from '../utils/core/logger';

interface UseVectorOptions {
    enabled: boolean;
    partnerId: string | null;
    /**
     * PostHog distinct_id for the current visitor. When provided, we call
     * `vector.identify(distinctId, ...)` so Vector echoes the id back in the
     * batch webhook payload (`contact.externalId`), letting the backend join
     * a contact directly to public.visitors.person_id.
     */
    distinctId?: string | null;
    /** Active site domain (e.g. `acme.com`). Required to POST /vector/link. */
    siteDomain?: string | null;
    /** Base API URL for the link POST (e.g. `https://ixvector-staging.run.app`). */
    apiUrl?: string | null;
    /** Optional known visitor email; forwarded to vector.identify + /link. */
    email?: string | null;
}

interface VectorIdEventPayload {
    vectorVisitorId?: string;
}

/**
 * Get the Vector.co partner ID from build-time globals or Vite env.
 *
 * Mirrors getSnitcherProfileId() in useSnitcherRadar.ts.
 */
function getVectorPartnerId(): string | null {
    if (typeof window !== 'undefined') {
        const definePluginId =
            typeof __INBOUNDX_VECTOR_PARTNER_ID__ !== 'undefined'
                ? __INBOUNDX_VECTOR_PARTNER_ID__
                : null;
        if (definePluginId) {
            return definePluginId;
        }
    }

    if (typeof import.meta !== 'undefined' && (import.meta as ImportMeta).env) {
        return (import.meta as ImportMeta).env.VITE_VECTOR_PARTNER_ID ?? null;
    }

    return null;
}

/**
 * Post a (person_id, vectorVisitorId) link through the account-context proxy
 * so the next batch webhook can join on visitors.vector_visitor_id instead of waiting for an
 * externalId echo. Best-effort: failures are logged at debug only — the
 * batch webhook still works without the link.
 */
function postVectorLink(args: {
    apiUrl: string;
    siteDomain: string;
    personId: string;
    vectorVisitorId: string;
    email: string | null;
}): void {
    const { apiUrl, siteDomain, personId, vectorVisitorId, email } = args;
    const url = `${apiUrl.replace(/\/+$/, '')}/account-context/id-link`;
    const body = JSON.stringify({
        site_domain: siteDomain,
        person_id: personId,
        vector_visitor_id: vectorVisitorId,
        email: email ?? undefined,
    });

    // Prefer sendBeacon for fire-and-forget reliability (survives page unload).
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        try {
            const blob = new Blob([body], { type: 'application/json' });
            if (navigator.sendBeacon(url, blob)) {
                return;
            }
        } catch (error) {
            logger.debug('[Vector] sendBeacon failed; falling back to fetch', error);
        }
    }

    fetch(url, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body,
        keepalive: true,
    }).catch((error) => {
        logger.debug('[Vector] vector-link POST failed', error);
    });
}

/**
 * Hook to load the Vector.co identification pixel.
 *
 * Vector.co de-anonymizes US visitors at the contact level. The pixel runs
 * passively — we only need to call vector.load(partnerId) once per page.
 *
 * No-op unless BOTH inputs resolve truthy:
 * - enabled: per-site staff-controlled flag from integrations.vector.enabled
 * - partnerId (or build-time-baked global): Rose-wide Vector partner ID
 *
 * When distinctId + siteDomain + apiUrl are also provided, this hook:
 *   1. Calls vector.identify(distinctId, { email }) so Vector tags the
 *      visitor with our PostHog distinct_id (echoed in webhook payload as
 *      contact.externalId).
 *   2. Subscribes to vector.on('visitor.id.found'|'visitor.id.created') and
 *      POSTs the resulting vectorVisitorId + person_id to /account-context/id-link so
 *      the backend can join the next batch webhook by vector_visitor_id.
 *
 * Idempotent: a second mount with the SDK already loaded is a no-op.
 */
export function useVector({
    enabled,
    partnerId,
    distinctId,
    siteDomain,
    apiUrl,
    email,
}: UseVectorOptions): void {
    useEffect(() => {
        if (!enabled) {
            return;
        }

        const resolvedPartnerId = partnerId ?? getVectorPartnerId();
        if (!resolvedPartnerId) {
            logger.debug('[Vector] No partner ID configured, skipping initialization');
            return;
        }

        if (typeof window === 'undefined' || typeof document === 'undefined') {
            return;
        }

        if (window.vector?.loaded) {
            logger.debug('[Vector] Already initialized, skipping');
            return;
        }

        // Install the queue stub. Mirrors the snippet provided by Vector.co —
        // calls to load/identify/on are queued until the real SDK boots and
        // replaces them.
        try {
            const stub: VectorPixelInstance = {
                q: [],
                loaded: true,
                load: (...args: unknown[]) => {
                    stub.q?.push(['load', args]);
                },
                identify: (...args: unknown[]) => {
                    stub.q?.push(['identify', args]);
                },
                on: (...args: unknown[]) => {
                    stub.q?.push(['on', args]);
                },
            };
            window.vector = stub;

            const script = document.createElement('script');
            script.type = 'text/javascript';
            script.async = true;
            script.src = 'https://cdn.vector.co/pixel.js';
            script.dataset.vectorPixel = 'true';
            script.onerror = () => {
                logger.debug('[Vector] Pixel script failed to load');
            };

            const firstScript = document.scripts[0];
            if (firstScript?.parentNode) {
                firstScript.parentNode.insertBefore(script, firstScript);
            } else {
                document.head.appendChild(script);
            }

            window.vector.load(resolvedPartnerId);
            logger.debug('[Vector] Pixel initialized with partner ID');
        } catch (error) {
            logger.debug('[Vector] Initialization failed:', error);
            return;
        }

        // Real-time link: tag Vector with our distinct_id and forward the
        // pixel's matched visitor UUID to ixvector so the next batch webhook
        // can join on visitors.vector_visitor_id.
        if (!distinctId || !siteDomain || !apiUrl) {
            return;
        }

        try {
            window.vector?.identify(distinctId, {
                email: email ?? undefined,
            });
        } catch (error) {
            logger.debug('[Vector] identify() failed', error);
        }

        const onVectorId = (event: VectorIdEventPayload | undefined) => {
            const vectorVisitorId = event?.vectorVisitorId;
            // The pixel cannot be unloaded: drop links after consent is withdrawn (IX-5465).
            if (!vectorVisitorId || !isIdentificationAllowed()) {
                return;
            }
            postVectorLink({
                apiUrl,
                siteDomain,
                personId: distinctId,
                vectorVisitorId,
                email: email ?? null,
            });
        };

        try {
            window.vector?.on('visitor.id.found', onVectorId);
            window.vector?.on('visitor.id.created', onVectorId);
        } catch (error) {
            logger.debug('[Vector] on() failed', error);
        }
    }, [enabled, partnerId, distinctId, siteDomain, apiUrl, email]);
}
